// This file is automatically generated. Do not edit it directly.
import { createClient } from '@supabase/supabase-js';
import type { Database } from './types';
import { brokeredPreviewStorage } from './previewAuthStorage';

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
const SUPABASE_PUBLISHABLE_KEY = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY || import.meta.env.VITE_SUPABASE_ANON_KEY;

// Validate Supabase configuration at initialization but be fault-tolerant
const validateSupabaseConfig = () => {
  const errors: string[] = [];

  if (!SUPABASE_URL) {
    errors.push('VITE_SUPABASE_URL is missing');
  }
  if (!SUPABASE_PUBLISHABLE_KEY) {
    errors.push('VITE_SUPABASE_PUBLISHABLE_KEY (or VITE_SUPABASE_ANON_KEY) is missing');
  }
  if (SUPABASE_PUBLISHABLE_KEY?.includes('placeholder') || SUPABASE_PUBLISHABLE_KEY?.includes('your_')) {
    errors.push('VITE_SUPABASE_PUBLISHABLE_KEY has a placeholder value - set actual key in environment');
  }

  if (errors.length > 0) {
    const errorMsg = `❌ Supabase configuration error:\n${errors.map(e => `  - ${e}`).join('\n')}`;
    console.error(errorMsg);
    return { ok: false, message: errorMsg };
  }
  return { ok: true };
};

// Validate on module load (non-throwing)
const validation = validateSupabaseConfig();

// Log environment summary (mask the key)
console.log('🔎 Supabase env:', {
  url: SUPABASE_URL ? SUPABASE_URL.replace(/https:\/\//, '').split('.')[0] : null,
  keyPresent: Boolean(SUPABASE_PUBLISHABLE_KEY),
  configured: validation.ok,
});

let _supabase: any = null;
let _supabaseFunctions: any = null;

if (validation.ok) {
  try {
    _supabase = createClient<Database>(SUPABASE_URL, SUPABASE_PUBLISHABLE_KEY, {
      auth: {
        storage: brokeredPreviewStorage(),
        persistSession: true,
        autoRefreshToken: true,
      },
      global: {
        headers: {
          // Will be set dynamically with Firebase UID
        },
      },
    });

    _supabaseFunctions = createClient<Database>(SUPABASE_URL, SUPABASE_PUBLISHABLE_KEY, {
      auth: {
        storage: brokeredPreviewStorage(),
        persistSession: true,
        autoRefreshToken: true,
      },
      global: {
        headers: {},
      },
    });

    console.log('✅ Supabase client initialized (safe)');
  } catch (err) {
    console.error('❌ Supabase createClient failed at runtime:', err);
  }
}

// If initialization failed, expose a safe stub that logs attempts instead of throwing during module load
const makeStubClient = () => {
  const handler = {
    get(target: any, prop: string) {
      if (!(prop in target)) {
        return (...args: any[]) => {
          console.warn(`Supabase stub called: ${prop}`, ...args);
          // Return a promise that resolves to a shape similar to supabase-js responses
          return Promise.resolve({ data: null, error: new Error('Supabase not configured') });
        };
      }
      return target[prop];
    },
  };
  return new Proxy({}, handler);
};

export const supabase = _supabase ?? makeStubClient();
export const supabaseFunctions = _supabaseFunctions ?? makeStubClient();

// Function to update Supabase headers with Firebase UID
export const setSupabaseFirebaseUID = async (firebaseUID: string | null) => {
  try {
    if (firebaseUID) {
      (supabase as any).rest.headers['x-firebase-uid'] = firebaseUID;
      console.log('🔐 Firebase UID header set for RLS:', `${firebaseUID.substring(0, 6)}...`);
      return true;
    } else {
      delete (supabase as any).rest.headers['x-firebase-uid'];
      console.log('🔐 Firebase UID header cleared');
      return true;
    }
  } catch (error) {
    console.error('❌ Failed to set Firebase UID header:', error);
    return false;
  }
};